<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { IconRadio, SelectInput } from '@/shared/ui';
  import { JUSTIFICATION_MAX_LENGTH } from '../model/const';

  const props = withDefaults(
    defineProps<{
      selectedOption: string | undefined;
      trainingOption: string | undefined;
      showAdditionalField: boolean;
      reprovalJustification: string | undefined;
      trainingJustification: string | undefined;
      selectedApprovalPosition: string | number | undefined;
      approvalPositionOptions: Array<{ id: number; name: string }>;
      approveOptionLabel?: string;
      rejectOptionLabel?: string;
      rejectPlaceholder?: string;
    }>(),
    {
      approveOptionLabel: 'Aprovar documento',
      rejectOptionLabel: 'Reprovar documento',
      rejectPlaceholder: 'Explique porque o documento foi reprovado',
    }
  );

  const reprovalJustification = ref<string>('');
  const trainingJustification = ref<string>('');

  const reprovalCurrentLength = computed(() => reprovalJustification.value.length);
  const trainingCurrentLength = computed(() => trainingJustification.value.length);

  watch(
    () => props.reprovalJustification,
    (newValue) => {
      if (newValue !== undefined) {
        reprovalJustification.value = newValue;
      }
    },
    { immediate: true }
  );

  watch(
    () => props.trainingJustification,
    (newValue) => {
      if (newValue !== undefined) {
        trainingJustification.value = newValue;
      }
    },
    { immediate: true }
  );

  watch(reprovalJustification, (newValue) => {
    emit('update:reprovalJustification', newValue);
  });

  watch(trainingJustification, (newValue) => {
    emit('update:trainingJustification', newValue);
  });

  const emit = defineEmits([
    'update:selectedOption',
    'update:trainingOption',
    'update:reprovalJustification',
    'update:trainingJustification',
    'update:selectedApprovalPosition',
  ]);

  defineExpose({
    getReprovalJustification() {
      return reprovalJustification.value;
    },
    getTrainingJustification() {
      return trainingJustification.value;
    },
  });
</script>

<template>
  <div>
    <div class="px-6 py-8">
      <div class="flex space-x-8 items-center">
        <IconRadio
          :value="'aprovar'"
          :model-value="selectedOption"
          name="documentDecision"
          :label="approveOptionLabel"
          @update:model-value="$emit('update:selectedOption', $event)"
        />
        <IconRadio
          :value="'reprovar'"
          :model-value="selectedOption"
          name="documentDecision"
          :label="rejectOptionLabel"
          @update:model-value="$emit('update:selectedOption', $event)"
        />
      </div>
    </div>

    <div
      v-if="selectedOption === 'aprovar'"
      class="border-t border-gray-02 w-full"
    />

    <div
      v-if="selectedOption === 'aprovar'"
      class="p-6"
    >
      <div class="text-gray-07 text-sm/5 w-full text-left font-semibold mb-2">
        Cargo relacionado à Etapa de Análise Crítica e Aprovação:
      </div>
      <div class="max-w-md">
        <SelectInput
          :model-value="selectedApprovalPosition ?? ''"
          :options="approvalPositionOptions"
          label="Selecione o cargo"
          @update:model-value="$emit('update:selectedApprovalPosition', $event)"
        />
      </div>
    </div>

    <div
      v-if="selectedOption"
      class="border-t border-gray-02 w-full"
    />

    <div
      v-if="selectedOption === 'reprovar'"
      class="p-6"
    >
      <div class="text-gray-07 text-sm/5 w-full text-left font-semibold mb-2">
        Justifique:
      </div>
      <textarea
        v-model="reprovalJustification"
        :placeholder="rejectPlaceholder"
        class="w-full h-33.5 border border-gray-02 rounded-lg p-4 resize-none"
        required
        :maxlength="JUSTIFICATION_MAX_LENGTH"
      />
      <p class="text-xs text-gray-07">
        {{ reprovalCurrentLength }}/{{ JUSTIFICATION_MAX_LENGTH }} caracteres
      </p>
    </div>

    <div
      v-if="showAdditionalField"
      class="px-6 py-8"
    >
      <div class="flex flex-col items-start gap-2">
        <label class="font-semibold">Será necessário treinamento?</label>
        <div class="flex items-center space-x-8">
          <IconRadio
            :value="'sim'"
            :model-value="trainingOption"
            name="trainingDecision"
            label="Sim"
            @update:model-value="$emit('update:trainingOption', $event)"
          />
          <IconRadio
            :value="'nao'"
            :model-value="trainingOption"
            name="trainingDecision"
            label="Não"
            @update:model-value="$emit('update:trainingOption', $event)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="trainingOption === 'nao' && showAdditionalField"
      class="p-6 border-t border-gray-02 w-full"
    >
      <div class="text-gray-07 text-sm/5 w-full text-left font-semibold mb-2">
        Justifique:
      </div>
      <textarea
        v-model="trainingJustification"
        placeholder="Explique porque não será necessário o treinamento"
        class="w-full h-33.5 border border-gray-02 rounded-lg p-4 resize-none"
        required
        :maxlength="JUSTIFICATION_MAX_LENGTH"
      />
      <p class="text-xs text-gray-07">
        {{ trainingCurrentLength }}/{{ JUSTIFICATION_MAX_LENGTH }} caracteres
      </p>
    </div>
  </div>
</template>
